import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { generationsService } from '../../services/generationsService';

const POLL_INTERVAL = 5000; // 5 seconds

const GiftNoteModal = ({
  isOpen,
  onClose,
  generation,
  setToastMessage,
  setToastActive,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [giftNotePdfUrl, setGiftNotePdfUrl] = useState(null);
  const [giftNoteStatus, setGiftNoteStatus] = useState(null);
  const [giftNoteText, setGiftNoteText] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  // Update local state when generation changes
  useEffect(() => {
    if (generation) {
      setGiftNotePdfUrl(generation.gift_note_pdf_url || null);
      setGiftNoteStatus(generation.gift_note_status || null);
      setGiftNoteText(generation.gift_note || '');
      setError('');
    }
  }, [generation]);

  // Polling logic
  useEffect(() => {
    const shouldPoll = isOpen && generation?.generation_id &&
      (giftNoteStatus === 'processing');

    if (shouldPoll) {
      pollRef.current = setInterval(async () => {
        try {
          const result = await generationsService.getGiftNote(generation.generation_id);
          const data = result?.data;
          if (data?.gift_note_status === 'completed' && data?.gift_note_pdf_url) {
            setGiftNotePdfUrl(data.gift_note_pdf_url);
            setGiftNoteStatus('completed');
            setError('');
            if (setToastMessage) setToastMessage('Gift note card generated successfully!');
            if (setToastActive) setToastActive(true);
          } else if (data?.gift_note_status === 'failed') {
            setGiftNoteStatus('failed');
            setError(data.gift_note_error || 'Generation failed');
          } else {
            setGiftNoteStatus(data?.gift_note_status || giftNoteStatus);
          }
        } catch (err) {
          console.error('Gift note poll error:', err);
        }
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isOpen, generation?.generation_id, giftNoteStatus]);

  const handleGenerate = async () => {
    const genId = generation?.generation_id;
    if (!genId) return;

    setIsGenerating(true);
    setError('');
    try {
      const result = await generationsService.generateGiftNote(genId);
      if (result?.success) {
        setGiftNoteStatus('processing');
        if (setToastMessage) setToastMessage('Gift note generation started!');
        if (setToastActive) setToastActive(true);
      } else {
        setError(result?.error || 'Failed to start gift note generation');
      }
    } catch (err) {
      setError(err?.message || 'Failed to start gift note generation');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPdf = () => {
    if (giftNotePdfUrl) {
      window.open(giftNotePdfUrl, '_blank');
    }
  };

  const isProcessing = giftNoteStatus === 'processing';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{ padding: '16px' }}>{'📝 Gift Note Card'}</div>}
      secondaryActions={[
        {
          content: 'Close',
          onAction: onClose,
        },
      ]}
      footer={<div style={{ padding: '16px' }}></div>}
    >
      <div style={{ padding: '16px' }}>
        <BlockStack gap="400">
          {/* Generation Info */}
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Generation: {generation?.generation_id || 'N/A'}
            </Text>
            <Text variant="bodySm" as="p" color="subdued">
              Kid: {generation?.kid_name || 'N/A'} | Order: {generation?.order_id || 'N/A'}
            </Text>
          </BlockStack>

          {/* Gift Note Message Preview */}
          {giftNoteText && (
            <div style={{
              backgroundColor: '#f4f0fa',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #d8cff0'
            }}>
              <Text variant="bodySm" as="p" tone="subdued" fontWeight="semibold">
                Gift Message:
              </Text>
              <Text variant="bodyMd" as="p" style={{ marginTop: '8px' }}>
                "{giftNoteText}"
              </Text>
            </div>
          )}

          {/* Error Banner */}
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}

          {/* Processing State */}
          {isProcessing && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              backgroundColor: '#f0f4ff',
              borderRadius: '12px',
              border: '2px solid #a0b4ff'
            }}>
              <BlockStack gap="200" align="center">
                <Spinner size="large" />
                <Text variant="headingSm" as="p" alignment="center">
                  ⏳ Generating gift note card...
                </Text>
                <Text variant="bodySm" as="p" alignment="center" color="subdued">
                  Rendering your message onto the card. This will update automatically.
                </Text>
              </BlockStack>
            </div>
          )}

          {/* Gift Note PDF (completed) */}
          {giftNotePdfUrl && !isProcessing ? (
            <BlockStack gap="300">
              <div style={{
                textAlign: 'center',
                padding: '30px 20px',
                backgroundColor: '#f0faf0',
                borderRadius: '12px',
                border: '2px solid #a0d0a0'
              }}>
                <BlockStack gap="200" align="center">
                  <Text variant="headingMd" as="p" alignment="center">
                    ✅
                  </Text>
                  <Text variant="bodyMd" as="p" alignment="center">
                    Gift note card generated successfully (2-page PDF)
                  </Text>
                </BlockStack>
              </div>
              <InlineStack gap="300" align="center">
                <Button onClick={handleDownloadPdf} variant="primary">
                  📥 Download Gift Note PDF
                </Button>
                <Button
                  onClick={handleGenerate}
                  loading={isGenerating}
                  variant="secondary"
                >
                  🔄 Regenerate
                </Button>
              </InlineStack>
            </BlockStack>
          ) : !isProcessing && (
            /* No PDF and not processing - show generate button */
            <BlockStack gap="300" align="center">
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                backgroundColor: '#f9f9f9',
                borderRadius: '12px',
                border: '2px dashed #d4d4d4'
              }}>
                <BlockStack gap="200" align="center">
                  <Text variant="headingMd" as="p" alignment="center">
                    📝
                  </Text>
                  <Text variant="bodyMd" as="p" alignment="center">
                    No gift note card generated yet
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Button onClick={handleGenerate} variant="primary" loading={isGenerating}>
                      ✨ Generate Gift Note Card
                    </Button>
                  </div>
                </BlockStack>
              </div>
            </BlockStack>
          )}
        </BlockStack>
      </div>
    </Modal>
  );
};

export default GiftNoteModal;
